import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Image, Save, AlertTriangle, Trash2, Database, Download, Cloud, CloudOff, Loader2, KeyRound, Fingerprint } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/lib/api";
import { requestBiometric } from "@/lib/biometric";
import { useAuth } from "@/context/AuthContext";

const empty = {
  azienda_nome: "", partita_iva: "", codice_fiscale: "", indirizzo: "",
  telefono: "", email: "", pec: "", sito_web: "", logo_base64: "",
  orario_entrata: "06:00", orario_uscita: "15:30",
};

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => { const s = r.result; res(typeof s === "string" ? s.split(",")[1] : ""); };
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function Impostazioni() {
  const { user } = useAuth();
  const [form, setForm] = useState(empty);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [backups, setBackups] = useState([]);
  const [backupBusy, setBackupBusy] = useState(false);
  const [drive, setDrive] = useState({ configured: false, connected: false });
  const [driveBusy, setDriveBusy] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdOld, setPwdOld] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdNew2, setPwdNew2] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);
  const fileRef = useRef();

  const loadBackups = async () => {
    try { const { data } = await api.get("/backups"); setBackups(data); } catch {}
  };
  const loadDrive = async () => {
    try { const { data } = await api.get("/drive/status"); setDrive(data); } catch {}
  };
  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm({ ...empty, ...data }));
    loadBackups();
    loadDrive();
    // Toast on return from OAuth
    const p = new URLSearchParams(window.location.search);
    if (p.get("drive") === "connected") {
      toast.success("Google Drive collegato!");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connectDrive = async () => {
    setDriveBusy(true);
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Google Drive non configurato lato server");
      setDriveBusy(false);
    }
  };
  const disconnectDrive = async () => {
    try { await api.post("/drive/disconnect"); toast.success("Google Drive scollegato"); loadDrive(); }
    catch { toast.error("Errore"); }
  };

  const openChangePwd = async () => {
    // Gate biometrico opzionale
    try { await requestBiometric(user?.id || "me", user?.name || user?.email || ""); } catch {}
    setPwdOpen(true);
    setPwdOld(""); setPwdNew(""); setPwdNew2("");
  };
  const submitChangePwd = async () => {
    if (pwdNew !== pwdNew2) { toast.error("Le due password nuove non coincidono"); return; }
    if (pwdNew.length < 8) { toast.error("Minimo 8 caratteri"); return; }
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: pwdOld, new_password: pwdNew });
      toast.success("Password aggiornata");
      setPwdOpen(false); setPwdOld(""); setPwdNew(""); setPwdNew2("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setPwdBusy(false); }
  };

  const runBackup = async () => {
    setBackupBusy(true);
    try {
      const { data } = await api.post("/backups/now");
      toast.success(`Backup creato: ${data.total_docs} documenti`);
      loadBackups();
    } catch { toast.error("Errore backup"); }
    finally { setBackupBusy(false); }
  };

  const save = async () => {
    try { await api.put("/settings", form); toast.success("Impostazioni salvate"); }
    catch { toast.error("Errore"); }
  };

  const onLogo = async (file) => {
    if (!file) return;
    const b64 = await fileToBase64(file);
    setForm((f) => ({ ...f, logo_base64: b64 }));
    toast.success("Logo caricato");
  };

  const doReset = async () => {
    if (confirm !== "FORMATTA") { toast.error("Scrivi FORMATTA per confermare"); return; }
    try {
      const { data } = await api.post("/system/reset");
      toast.success(`Dati azzerati: ${Object.values(data.deleted).reduce((s, v) => s + v, 0)} record rimossi`);
      setResetOpen(false); setConfirm("");
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Impostazioni</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Dati Azienda & PDF</h1>
        <p className="text-muted-foreground mt-1">Logo e dati fiscali compaiono su tutti i PDF (fatture, rendiconti).</p>
      </div>

      <Card className="card-elevated">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-24 w-24 rounded-lg bg-secondary flex items-center justify-center overflow-hidden border border-border">
              {form.logo_base64 ? (
                <img src={`data:image/png;base64,${form.logo_base64}`} alt="logo" className="h-full w-full object-contain" />
              ) : (
                <Image className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onLogo(e.target.files?.[0])} data-testid="logo-file" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="logo-upload"><Image className="h-4 w-4 mr-2" /> Carica logo</Button>
              {form.logo_base64 && <Button variant="ghost" onClick={() => setForm({ ...form, logo_base64: "" })} className="ml-2">Rimuovi</Button>}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Ragione sociale</Label><Input data-testid="s-nome" value={form.azienda_nome} onChange={(e) => setForm({ ...form, azienda_nome: e.target.value })} /></div>
            <div><Label>Partita IVA</Label><Input value={form.partita_iva} onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} /></div>
            <div><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} /></div>
            <div><Label>Telefono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>PEC</Label><Input value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} /></div>
            <div><Label>Sito web</Label><Input value={form.sito_web} onChange={(e) => setForm({ ...form, sito_web: e.target.value })} /></div>
          </div>

          <div className="pt-4 border-t border-border">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Orario di lavoro standard operai</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Entrata</Label><Input data-testid="s-entrata" type="time" value={form.orario_entrata} onChange={(e) => setForm({ ...form, orario_entrata: e.target.value })} /></div>
              <div><Label>Uscita</Label><Input data-testid="s-uscita" type="time" value={form.orario_uscita} onChange={(e) => setForm({ ...form, orario_uscita: e.target.value })} /></div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Oltre questo orario le ore vengono calcolate come straordinari.</p>
          </div>

          <Button data-testid="s-save" onClick={save} className="h-11"><Save className="h-4 w-4 mr-2" /> Salva</Button>
        </CardContent>
      </Card>

      <Card className="card-elevated">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            <div className="font-bold">Backup automatico database</div>
          </div>
          <p className="text-sm text-muted-foreground">
            Ogni notte alle <strong>02:00</strong> (ora italiana) il sistema salva automaticamente uno snapshot completo
            di tutti i dati (ultimi 30 backup conservati). Puoi anche crearne uno manualmente ora o scaricare quelli esistenti.
          </p>
          <Button data-testid="backup-now" onClick={runBackup} disabled={backupBusy}>
            <Database className="h-4 w-4 mr-2" /> {backupBusy ? "Backup in corso..." : "Esegui backup ora"}
          </Button>

          {/* Google Drive sync */}
          <div className="pt-3 border-t border-border space-y-2">
            <div className="flex items-center gap-2">
              {drive.connected ? (
                <Cloud className="h-4 w-4 text-emerald-600" />
              ) : (
                <CloudOff className="h-4 w-4 text-muted-foreground" />
              )}
              <div className="text-sm font-semibold">Copia su Google Drive</div>
            </div>
            {drive.connected ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Collegato come <strong>{drive.email || "account Google"}</strong>. Ogni backup (notturno e manuale)
                  viene caricato automaticamente nella cartella <strong>{drive.folder_name}</strong> del tuo Drive.
                </p>
                <Button data-testid="drive-disconnect" variant="outline" size="sm" onClick={disconnectDrive}>
                  <CloudOff className="h-4 w-4 mr-2" /> Scollega Drive
                </Button>
              </>
            ) : drive.configured ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Collega il tuo account Google: in automatico ogni dump notturno viene salvato in una cartella
                  dedicata del tuo Drive. Se anche l'infrastruttura smettesse di funzionare, i dati restano al sicuro.
                </p>
                <Button data-testid="drive-connect" onClick={connectDrive} disabled={driveBusy}>
                  {driveBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Cloud className="h-4 w-4 mr-2" />}
                  Collega Google Drive
                </Button>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">
                Google Drive non è ancora configurato lato server. Chiedi all'amministratore di impostare le
                credenziali OAuth (<code>GOOGLE_DRIVE_CLIENT_ID / SECRET / REDIRECT_URI</code>).
              </p>
            )}
          </div>

          {backups.length > 0 && (
            <div className="pt-3 border-t border-border space-y-1 max-h-64 overflow-y-auto">
              {backups.map((b) => (
                <div key={b.id} className="flex items-center justify-between gap-2 py-1 text-xs">
                  <div className="flex-1">
                    <div className="font-semibold">{new Date(b.created_at).toLocaleString("it-IT")}</div>
                    <div className="text-muted-foreground">
                      {b.total_docs} documenti {b.manuale ? "· manuale" : "· automatico"}
                      {b.drive_url ? <span className="ml-2 text-emerald-600">· su Drive</span> : null}
                    </div>
                  </div>
                  {b.drive_url && (
                    <a href={b.drive_url} target="_blank" rel="noreferrer">
                      <Button variant="ghost" size="sm" title="Apri su Drive"><Cloud className="h-3 w-3" /></Button>
                    </a>
                  )}
                  <a href={`${API}/backups/${b.id}/download`} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm"><Download className="h-3 w-3" /></Button>
                  </a>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="card-elevated">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            <div className="font-bold">La mia password</div>
          </div>
          <p className="text-sm text-muted-foreground">
            Cambia la tua password. Verrà richiesta un'autenticazione biometrica (Face ID / impronta digitale) se il tuo dispositivo la supporta.
          </p>
          <Button data-testid="change-pwd-btn" onClick={openChangePwd}>
            <Fingerprint className="h-4 w-4 mr-2" /> Cambia password
          </Button>
        </CardContent>
      </Card>

      <Card className="card-elevated border-destructive">
        <CardContent className="p-6 space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <div className="font-bold text-destructive">Zona pericolosa - Reset dati</div>
          </div>
          <p className="text-sm text-muted-foreground">
            Rimuove tutti i cantieri, fatture, presenze, rendiconti, timbrature, computi, DDT,
            fornitori, clienti, appuntamenti e report AI. <strong>Mantiene</strong> gli utenti e le impostazioni azienda.
            Usa questa opzione quando sei pronto a partire operativo da zero.
          </p>
          <Button data-testid="reset-btn" variant="destructive" onClick={() => setResetOpen(true)}>
            <Trash2 className="h-4 w-4 mr-2" /> Formatta tutti i dati operativi
          </Button>
        </CardContent>
      </Card>

      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cambia la mia password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Password attuale</Label>
              <Input data-testid="pwd-old" type="password" value={pwdOld} onChange={(e) => setPwdOld(e.target.value)} />
            </div>
            <div>
              <Label>Nuova password (min 8 caratteri)</Label>
              <Input data-testid="pwd-new" type="password" value={pwdNew} onChange={(e) => setPwdNew(e.target.value)} />
            </div>
            <div>
              <Label>Ripeti nuova password</Label>
              <Input data-testid="pwd-new2" type="password" value={pwdNew2} onChange={(e) => setPwdNew2(e.target.value)} />
            </div>
            <Button data-testid="pwd-save" onClick={submitChangePwd} disabled={pwdBusy || !pwdOld || pwdNew.length < 8} className="w-full h-11">
              {pwdBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Aggiorna password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-destructive">Conferma reset totale</DialogTitle></DialogHeader>
          <p className="text-sm">
            Stai per cancellare <strong>definitivamente</strong> tutti i dati operativi (resta solo l'anagrafica utenti
            e le impostazioni azienda). <strong>Operazione irreversibile.</strong>
          </p>
          <p className="text-sm">Scrivi <strong>FORMATTA</strong> qui sotto per confermare:</p>
          <Input data-testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())} placeholder="FORMATTA" />
          <Button data-testid="reset-exec" variant="destructive" onClick={doReset} disabled={confirm !== "FORMATTA"}>
            Procedi con il reset
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
